/**
 * payments-infra client — babylon_meshdom
 *
 * All calls go to the shared payments-infra Lambda Function URLs.
 * Env vars are set per stage in .env.testing / .env.prod.
 *
 * Usage:
 *   import { startCheckout, getSubscription, openBillingPortal, trackUsage } from '@/lib/payments'
 */

const APP_ID = 'meshdom'

const CHECKOUT_URL     = process.env.REACT_APP_PAYMENTS_CHECKOUT_URL ?? ''
const SUBSCRIPTION_URL = process.env.REACT_APP_PAYMENTS_SUBSCRIPTION_URL ?? ''
const PORTAL_URL       = process.env.REACT_APP_PAYMENTS_PORTAL_URL ?? ''
const USAGE_URL        = process.env.REACT_APP_PAYMENTS_USAGE_URL ?? ''

export interface SubscriptionResult {
  appId:             string
  userId:            string
  tier:              'free' | 'pro' | 'enterprise'
  status:            string | null
  billing:           'monthly' | 'yearly' | null
  limits:            Record<string, number | boolean | string>
  usage:             Record<string, number>
  currentPeriodEnd:  string | null
  cancelAtPeriodEnd: boolean
  updatedAt:         string | null
}

export interface UsageResult {
  allowed:   boolean
  tier:      string
  featureKey: string
  used:      number
  limit:     number
  remaining: number
  message?:  string
}

/** Redirect user to Stripe Checkout for the given tier/billing combo. */
export async function startCheckout(
  userId: string,
  userEmail: string | undefined,
  tier: 'pro' | 'enterprise',
  billing: 'monthly' | 'yearly',
): Promise<void> {
  if (!CHECKOUT_URL) throw new Error('REACT_APP_PAYMENTS_CHECKOUT_URL not set')

  const origin     = window.location.origin
  const successUrl = `${origin}/profile/me?checkout=success`
  const cancelUrl  = `${origin}/profile/me?checkout=cancel`

  const res = await fetch(CHECKOUT_URL, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ appId: APP_ID, userId, userEmail, tier, billing, successUrl, cancelUrl }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? `Checkout failed (${res.status})`)
  }

  const { url } = await res.json() as { url: string }
  window.location.href = url
}

/** Fetch the current user's subscription tier, limits, and usage. */
export async function getSubscription(userId: string): Promise<SubscriptionResult> {
  if (!SUBSCRIPTION_URL) throw new Error('REACT_APP_PAYMENTS_SUBSCRIPTION_URL not set')

  const res = await fetch(SUBSCRIPTION_URL, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ appId: APP_ID, userId }),
  })

  if (!res.ok) throw new Error(`Failed to fetch subscription (${res.status})`)
  return res.json() as Promise<SubscriptionResult>
}

/** Open Stripe Customer Portal so user can manage/cancel their subscription. */
export async function openBillingPortal(userId: string): Promise<void> {
  if (!PORTAL_URL) throw new Error('REACT_APP_PAYMENTS_PORTAL_URL not set')

  const returnUrl = `${window.location.origin}/profile/me`

  const res = await fetch(PORTAL_URL, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ appId: APP_ID, userId, returnUrl }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? `Portal failed (${res.status})`)
  }

  const { url } = await res.json() as { url: string }
  window.location.href = url
}

/**
 * Track a feature usage event and check if the user is within their limit.
 * Call this BEFORE performing a gated action (e.g. creating a pattern).
 * If `allowed` is false, show an upgrade prompt instead of proceeding.
 */
export async function trackUsage(userId: string, featureKey: string): Promise<UsageResult> {
  if (!USAGE_URL) throw new Error('REACT_APP_PAYMENTS_USAGE_URL not set')

  const res = await fetch(USAGE_URL, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ appId: APP_ID, userId, featureKey }),
  })

  if (!res.ok) throw new Error(`Failed to track usage (${res.status})`)
  return res.json() as Promise<UsageResult>
}
